import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AgenciaOption {
  id: string;
  nome: string;
  moeda_padrao: string | null;
}

/**
 * Lista de agências (id + nome) para dropdowns, com cache compartilhado.
 * Antes Vagas.tsx e VagaForm.tsx faziam cada um seu próprio fetch cru
 * (useEffect/useState, sem cache): abrir o diálogo "Nova Vaga" disparava uma
 * 2ª query idêntica de agências em /vagas. O hook deduplica as chamadas.
 *
 * Key DEDICADA ["agencias", "options"] — NÃO a key ["agencias"] usada por
 * outras telas. Isso é proposital: o cache do TanStack é last-writer-wins por
 * key, independente de staleTime. Se este payload enxuto (id, nome) escrevesse
 * em ["agencias"], sobrescreveria in-place as linhas completas (email,
 * telefone, categorias...) de consumidores como Agencias, RadarAgencias e
 * IndicarAgenciaDialog que fazem select() completo na MESMA key — eles
 * perderiam colunas até o próximo refetch. A key dedicada isola os shapes.
 */
export function useAgencias() {
  return useQuery({
    queryKey: ["agencias", "options"],
    staleTime: 15 * 60 * 1000,
    queryFn: async (): Promise<AgenciaOption[]> => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome, moeda_padrao")
        .order("nome");
      if (error) throw error;
      return (data || []) as AgenciaOption[];
    },
  });
}

/**
 * Invalida o cache de agências após criar/editar/excluir uma agência. Usa a
 * key-prefixo ["agencias"], que (match por prefixo do TanStack) cobre TANTO
 * ["agencias"] (telas com shape completo) QUANTO ["agencias", "options"]
 * (dropdowns) — assim ambos os formatos revalidam de uma vez.
 */
export function useAgenciasInvalidate() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["agencias"] });
  }, [queryClient]);
}

/**
 * Marca/desmarca uma agência como favorita. Favoritas sobem para o topo das
 * listagens (tela de Agências e seletor de agências do match). Invalida o
 * prefixo ["agencias"] para revalidar todos os shapes.
 */
export function useToggleAgenciaFavorita() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; favorita: boolean }) => {
      const { error } = await supabase
        .from("agencias")
        .update({ favorita: input.favorita })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencias"] });
    },
  });
}
